import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BreakPointService, ButtonComponent, Device, SlottedModalComponent } from '@woolworthsnz/styleguide';
import { map } from 'rxjs';
import { MealPlanPreference } from '../../models';
import { MealPlanPreferenceName } from '../choose-meal-plan';
import { HouseholdPreferenceComponentEvent } from '../meal-plan-household-preference/meal-plan-household-preference.interface';
import { MealPlanModifyPreferencesStore, MealPlanPreferences } from './meal-plan-modify-preferences.store';
import { MealPlanDietaryPreferenceComponent } from '../meal-plan-dietary-preference/meal-plan-dietary-preference.component';
import { MealPlanHouseholdPreferenceComponent } from '../meal-plan-household-preference/meal-plan-household-preference.component';
import { NgIf, NgTemplateOutlet, AsyncPipe } from '@angular/common';

@Component({
	selector: 'recipe-meal-plan-modify-preferences',
	templateUrl: './meal-plan-modify-preferences.component.html',
	styleUrls: ['./meal-plan-modify-preferences.component.scss'],
	providers: [MealPlanModifyPreferencesStore],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, SlottedModalComponent, MealPlanHouseholdPreferenceComponent, MealPlanDietaryPreferenceComponent, NgTemplateOutlet, ButtonComponent, AsyncPipe]
})
export class MealPlanModifyPreferencesComponent implements OnInit {
	@Input() preferences: MealPlanPreference[];
	@Input() dietaryOptions: { key: number; text: string }[];
	@Output() save = new EventEmitter<MealPlanPreference[]>();
	@Output() close = new EventEmitter<void>();
	public isMobileDevice$ = this.breakpointService.device$.pipe(map((device) => device === Device.MOBILE));
	public preferenceName = MealPlanPreferenceName;
	public preferences$ = this.store.selectPreferences$;
	private hasChangedPreference = false;

	constructor(private store: MealPlanModifyPreferencesStore, private breakpointService: BreakPointService) { }

	public ngOnInit(): void {
		this.reset();
	}

	public saveHouseholdPreference(
		newPreference: HouseholdPreferenceComponentEvent,
		preferences: MealPlanPreferences
	): void {
		this.hasChangedPreference = true;
		this.store.updatePreferences({
			...preferences,
			[MealPlanPreferenceName.people]: {
				...preferences[MealPlanPreferenceName.people],
				[newPreference.type]: newPreference.value,
			},
		});
	}

	public saveDietaryPreference(newPreference: string[], preferences: MealPlanPreferences): void {
		this.hasChangedPreference = true;
		this.store.updatePreferences({ ...preferences, [MealPlanPreferenceName.dietaryNeeds]: newPreference });
	}

	public applyChanges(preferences: MealPlanPreferences): void {
		if (!this.hasChangedPreference) {
			return;
		}
		this.save.emit(this.preferencesToArray(preferences));
	}

	public closeModal(): void {
		this.close.emit();
	}

	public reset(): void {
		this.setPreferences(this.preferences);
	}

	private setPreferences(preferences: MealPlanPreference[]): void {
		this.store.updatePreferences(this.preferencesToObject(preferences));
	}

	private preferencesToArray(preferences: MealPlanPreferences): MealPlanPreference[] {
		return (Object.keys(preferences) as MealPlanPreferenceName[]).map((key) => ({
			name: key,
			value: preferences[key],
		}));
	}

	private preferencesToObject(preferences: MealPlanPreference[]): MealPlanPreferences {
		return preferences.reduce(
			(acc, preference) => ({ ...acc, [preference.name]: preference.value }),
			{} as MealPlanPreferences
		);
	}
}
